import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoOptions, MaskitoPreprocessor } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';

@Component({
  selector: 'app-numeric-string-input',
  templateUrl: './numeric-string-input.component.html',
  styleUrls: ['./numeric-string-input.component.scss'],
  standalone: true,
  imports: [MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericStringInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NumericStringInputComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Input() maxLength: number | null = null;
  @Output() valueChange = new EventEmitter<string | null>();
  @Output() inputFocused = new EventEmitter<void>();

  private cdr = inject(ChangeDetectorRef);

  public value: string | null = null;
  public disabled: boolean = false;
  public maskOptions: MaskitoOptions = this.buildMaskOptions(null);

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxLength) {
      this.maskOptions = this.buildMaskOptions(this.maxLength);
    }
  }

  public writeValue(value: string | null): void {
    this.value = value ?? null;
    this.cdr.markForCheck();
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const value = inputValue === '' ? null : inputValue;
    this.value = value;
    this.valueChange.emit(value);
    this.onChange(value);
  }

  public handleBlur(): void {
    this.onTouched();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  private buildMaskOptions(maxLength: number | null): MaskitoOptions {
    const stripNonDigits: MaskitoPreprocessor = ({ elementState, data }, actionType) => ({
      elementState,
      data: actionType === 'insert' ? data.replace(/\D/g, '') : data
    });
    return {
      mask: maxLength === null ? /^\d*$/ : new RegExp(`^\\d{0,${maxLength}}$`),
      preprocessors: [stripNonDigits]
    };
  }
}
